// Every PendingEdits scope id the company surfaces register under, in one place so that they can be
// seen to be different.
//
// A SCOPE ID IS A KEY IN ONE FLAT REGISTRY (lib/pending-edits). Two editors registering the same id
// do not both appear in it: the second overwrites the first, and the first's unsaved work then has
// no `dirty`, no `save` and no `discard` anywhere in the app, because the SaveBar, UnsavedGuard and
// the acting-company gate all read that single entry. Nothing detects the collision at runtime and
// nothing about the screen looks wrong, so the ids are listed together here and pinned distinct by
// a test rather than spelled inline at four call sites.
//
// FOUR, BECAUSE FOUR THINGS ON /c/:id HOLD WORK THAT IS NOT WRITTEN YET. They are separate scopes
// rather than one company-wide scope because each is a separate write to genesis with its own
// refusal: `save` on the profile scope must not also add a member, and a member add that genesis
// refuses must not report the logo as unsaved.
//
// TWO OF THEM NO LONGER BELONG TO A SECTION. The profile and the logo used to be editors appended
// below the page, each registering its own id on mount; both are gone, and the fields they held are
// edited where their values are rendered. The scopes did not go with them — the page registers the
// pair for as long as it is in edit mode (CompanyEditingScopes in CompanyPage) — because what a
// scope is for has nothing to do with where the field is drawn: it is one write, and one refusal.
//
// ALL FOUR ARE REGISTERED ON /c/new TOO, AND NONE OF THEM IS EVER DIRTY THERE (@adr SPARK-0038-creating-a-company-is-the-company-page-before-it-exists@2). /c/new is
// this same page before genesis has an id for it, so the same four editors are on screen holding the
// same four kinds of work — and the create button reaches that work through this registry, which is
// the only seam between a page-level control and state an editor owns.
//
// What it does NOT do is report any of it as unsaved. `dirty` is what the SaveBar, UnsavedGuard and
// the acting-company gate read, and none of the three belongs to a company that does not exist: the
// bar would stand beside "Create company" offering the same commit under a second name, and the
// guard would defend a form holding nothing genesis knows about. That is the same judgement the
// deleted create page made when it registered no scope at all, kept through the merge — see
// CompanyEditingScopes in CompanyPage, and the two editors that register the other pair.

/** The company's own fields on /c/:id, edited in place (use-company-profile-editing). */
export const COMPANY_SCOPE_PROFILE = "company-profile";
/** An image picked but not uploaded (use-company-logo-editing). */
export const COMPANY_SCOPE_LOGO = "company-logo";
/** An expertise label and degree typed but not staged, plus everything staged on the list
 *  (CompanyCompetencyEditor). Not "a competency field": v4 retired the shared catalogue, so what is
 *  drafted here is this company's own free text. */
export const COMPANY_SCOPE_COMPETENCY = "company-competency";
/** A person picked but not added to the company (CompanyMemberEditor). */
export const COMPANY_SCOPE_MEMBER = "company-member";

/** All of them, for the test that pins them distinct. */
export const COMPANY_PENDING_SCOPES = [
  COMPANY_SCOPE_PROFILE,
  COMPANY_SCOPE_LOGO,
  COMPANY_SCOPE_COMPETENCY,
  COMPANY_SCOPE_MEMBER,
] as const;
